/**
 * 旧版 Android WebView（如 Android 11 自带的 Chrome 83）缺失的运行时 API 兜底。
 *
 * Next 的 babel-loader 会 exclude node_modules，babel.config.json 里的
 * useBuiltIns: "usage" 只对 src 下的代码注入 core-js，第三方包用到的新 API
 * 一个都不会补，所以这里手动补齐。必须在任何业务代码之前执行（见 _app.page.jsx）。
 *
 * 覆盖的第三方用法：
 * - react-markdown@10：Object.hasOwn（Chrome 93+）
 * - mermaid@10：structuredClone（98+）、String.prototype.replaceAll（85+）、
 *   Array.prototype.findLast / findLastIndex（97+）
 * - Array/String.prototype.at（92+）
 *
 * Node 14 同样缺 Object.hasOwn / structuredClone，SSR 也依赖这里。
 */

/* eslint-disable no-extend-native */

// 旧 WebView 也可能缺 globalThis，先取到全局对象。
const globalRoot =
  (typeof globalThis !== 'undefined' && globalThis) ||
  (typeof window !== 'undefined' && window) ||
  (typeof global !== 'undefined' && global) ||
  {}

const define = (target, name, value) => {
  if (typeof target[name] === 'function') return
  Object.defineProperty(target, name, {
    configurable: true,
    writable: true,
    enumerable: false,
    value,
  })
}

// ── Object.hasOwn ────────────────────────────────────────────────────────────
define(Object, 'hasOwn', function hasOwn(object, property) {
  if (object === null || object === undefined) {
    throw new TypeError('Cannot convert undefined or null to object')
  }
  return Object.prototype.hasOwnProperty.call(Object(object), property)
})

// ── 相对索引：Array.prototype.at / String.prototype.at ───────────────────────
const relativeIndex = (length, index) => {
  let i = Number(index)
  if (i !== i) i = 0 // NaN
  else if (i === Infinity || i === -Infinity) return -1
  else i = i < 0 ? Math.ceil(i) : Math.floor(i)
  const resolved = i >= 0 ? i : length + i
  return resolved >= 0 && resolved < length ? resolved : -1
}

define(Array.prototype, 'at', function at(index) {
  if (this === null || this === undefined) {
    throw new TypeError('Array.prototype.at called on null or undefined')
  }
  const self = Object(this)
  const i = relativeIndex(self.length >>> 0, index)
  return i === -1 ? undefined : self[i]
})

define(String.prototype, 'at', function at(index) {
  if (this === null || this === undefined) {
    throw new TypeError('String.prototype.at called on null or undefined')
  }
  const self = String(this)
  const i = relativeIndex(self.length, index)
  return i === -1 ? undefined : self[i]
})

// ── Array.prototype.findLast / findLastIndex ─────────────────────────────────
define(Array.prototype, 'findLastIndex', function findLastIndex(predicate, thisArg) {
  if (this === null || this === undefined) {
    throw new TypeError('Array.prototype.findLastIndex called on null or undefined')
  }
  if (typeof predicate !== 'function') throw new TypeError(`${predicate} is not a function`)
  const self = Object(this)
  for (let i = (self.length >>> 0) - 1; i >= 0; i -= 1) {
    if (predicate.call(thisArg, self[i], i, self)) return i
  }
  return -1
})

define(Array.prototype, 'findLast', function findLast(predicate, thisArg) {
  const index = Array.prototype.findLastIndex.call(this, predicate, thisArg)
  return index === -1 ? undefined : Object(this)[index]
})

// ── String.prototype.replaceAll ──────────────────────────────────────────────
// mermaid 既用字符串也用带 /g 的正则调用它，两种入参都要支持。
define(String.prototype, 'replaceAll', function replaceAll(pattern, replacement) {
  if (this === null || this === undefined) {
    throw new TypeError('String.prototype.replaceAll called on null or undefined')
  }

  if (pattern instanceof RegExp) {
    if (!pattern.global) {
      throw new TypeError('replaceAll must be called with a global RegExp')
    }
    return String(this).replace(pattern, replacement)
  }

  const self = String(this)
  const search = String(pattern)
  const isFn = typeof replacement === 'function'
  const replaceStr = isFn ? '' : String(replacement)

  // 计算某个命中位置上要插入的文本，兼容函数入参与 $& / $$ / $` / $' 替换模式。
  const expand = (hit) =>
    isFn
      ? String(replacement(search, hit, self))
      : replaceStr.replace(/\$([$&`'])/g, (_, token) => {
          if (token === '$') return '$'
          if (token === '&') return search
          if (token === '`') return self.slice(0, hit)
          return self.slice(hit + search.length)
        })

  // 空 pattern 时原生会在每个字符前后各插入一次，单独处理以免死循环。
  if (search === '') {
    let result = expand(0)
    for (let i = 0; i < self.length; i += 1) {
      result += self[i] + expand(i + 1)
    }
    return result
  }

  let result = ''
  let cursor = 0
  let hit = self.indexOf(search, cursor)

  while (hit !== -1) {
    result += self.slice(cursor, hit) + expand(hit)
    cursor = hit + search.length
    hit = self.indexOf(search, cursor)
  }

  return result + self.slice(cursor)
})

// ── structuredClone ──────────────────────────────────────────────────────────
// mermaid 只用它深拷贝纯数据（配置对象、坐标点数组），这里做一个够用的深拷贝，
// 覆盖 Date / RegExp / Map / Set / Array / 普通对象，并处理循环引用。
if (typeof globalRoot.structuredClone !== 'function') {
  const deepClone = (value, seen) => {
    if (value === null || typeof value !== 'object') {
      if (typeof value === 'function') {
        throw new Error('structuredClone: function could not be cloned')
      }
      return value
    }

    if (seen.has(value)) return seen.get(value)

    if (value instanceof Date) return new Date(value.getTime())
    if (value instanceof RegExp) return new RegExp(value.source, value.flags)

    if (Array.isArray(value)) {
      const arr = []
      seen.set(value, arr)
      for (let i = 0; i < value.length; i += 1) arr[i] = deepClone(value[i], seen)
      return arr
    }

    if (typeof Map !== 'undefined' && value instanceof Map) {
      const map = new Map()
      seen.set(value, map)
      value.forEach((v, k) => map.set(deepClone(k, seen), deepClone(v, seen)))
      return map
    }

    if (typeof Set !== 'undefined' && value instanceof Set) {
      const set = new Set()
      seen.set(value, set)
      value.forEach((v) => set.add(deepClone(v, seen)))
      return set
    }

    const obj = {}
    seen.set(value, obj)
    Object.keys(value).forEach((key) => {
      obj[key] = deepClone(value[key], seen)
    })
    return obj
  }

  globalRoot.structuredClone = function structuredClone(value) {
    return deepClone(value, new Map())
  }
}

